import React, { useEffect, useState, useCallback } from "react";
import { TL } from "../i18n";
import client, { apiErr } from "../lib/api";
import { Card, Badge, Button, Select } from "./ui";
import { encryptBackup, decryptBackup } from "../lib/e2e";
import { Network, KeyRound, Check, X, Radio, Play, Eye, ScrollText, Lock, Send, Inbox, Plus, Trash2, Shield } from "lucide-react";

const appL = { family_tree: "Family Tree", benefits: "Benefits", currency: "Currency", holdings: "Holdings" };
const OPS = [
  { key: "read", label: "Reads / collate" },
  { key: "write", label: "Write operations" },
  { key: "scheduled", label: "Scheduled / automated" },
  { key: "wipe", label: "Wipes" },
];
const optinOf = (m) => {
  const o = m.optin || {};
  const legacy = !!m.scheduled_optin;
  return { read: !!o.read, write: !!o.write, scheduled: o.scheduled ?? legacy, wipe: !!o.wipe };
};

export default function ClusterInvites() {
  const [links, setLinks] = useState({ as_prime: [], as_member: [] });
  const [codes, setCodes] = useState({});
  const [msg, setMsg] = useState("");

  const load = useCallback(() => client.get("/my-cluster-links").then((r) => setLinks(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const respond = async (m, action) => {
    try {
      await client.post(`/cluster-members/${m.id}/respond`, { action, code: (codes[m.id] || "").trim() });
      setMsg(action === "accept" ? "Secure link established. You are opted OUT of every operation until you choose otherwise." : "Invite declined.");
      load();
    } catch (e) { setMsg(apiErr(e)); }
  };
  const setOpt = async (m, key, val) => {
    try { await client.post(`/cluster-members/${m.id}/optin`, { [key]: val }); load(); }
    catch (e) { setMsg(apiErr(e)); }
  };

  const pending = links.as_member.filter((m) => m.status === "invited");
  const active = links.as_member.filter((m) => m.status === "active");
  if (pending.length === 0 && active.length === 0 && links.as_prime.length === 0) return null;

  return (
    <Card className="space-y-4" data-testid="cluster-invites">
      <div className="flex items-center gap-2">
        <Network size={18} className="text-[var(--primary)]" />
        <span className="font-head text-lg">{TL("Cluster links")}</span>
        <span className="text-xs opacity-60">{TL("Peer coordination between instances. You alone control what your instance joins — the operator can never see these choices.")}</span>
      </div>
      {msg && <p className="text-sm text-[var(--primary)]" data-testid="cluster-invite-msg">{msg}</p>}

      {links.as_prime.map((c) => (
        <React.Fragment key={c.id}>
          <PrimePanel cluster={c} onMsg={setMsg} />
          <SealedPanel cid={c.id} name={c.name} isPrime={true} apps={Object.keys(appL)} onMsg={setMsg} />
        </React.Fragment>
      ))}

      {pending.map((m) => (
        <div key={m.id} className="rounded-xl border border-amber-400/50 bg-amber-400/5 p-3" data-testid={`cluster-invite-${m.id}`}>
          <div className="text-sm">{TL("Cluster")} <b>“{m.cluster_name}”</b> (Prime: {m.prime_label}) invites this instance to link with <b>{m.access === "both" ? "read+write" : m.access}</b> access on {(m.apps || []).map((a) => appL[a] || a).join(", ")}.</div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <div className="relative">
              <KeyRound size={14} className="absolute left-2 top-1/2 -translate-y-1/2 opacity-50" />
              <input value={codes[m.id] || ""} onChange={(e) => setCodes({ ...codes, [m.id]: e.target.value })} placeholder={TL("Pairing code")}
                data-testid={`cluster-invite-code-${m.id}`} className="rounded-md border border-black/15 dark:border-white/15 bg-transparent py-1.5 ps-7 pe-3 text-sm font-mono tracking-widest uppercase" />
            </div>
            <Button onClick={() => respond(m, "accept")} data-testid={`cluster-invite-accept-${m.id}`}><Check size={15} /> {TL("Accept & link")}</Button>
            <Button variant="ghost" onClick={() => respond(m, "decline")} data-testid={`cluster-invite-decline-${m.id}`}><X size={15} /> {TL("Decline")}</Button>
          </div>
        </div>
      ))}

      {active.map((m) => {
        const o = optinOf(m);
        return (
          <div key={m.id} className="rounded-xl border border-black/10 p-3 dark:border-white/10" data-testid={`cluster-active-${m.id}`}>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge color="success">{TL("Linked")}</Badge> “{m.cluster_name}” — {m.access === "both" ? "read+write" : m.access} on {(m.apps || []).map((a) => appL[a] || a).join(", ")}.
              <button onClick={() => respond(m, "decline")} className="ms-auto text-xs opacity-60 hover:text-[#DA1E28]" data-testid={`cluster-unlink-${m.id}`}>{TL("Unlink")}</button>
            </div>
            <div className="mt-2 text-xs opacity-60">{TL("Opt this instance in/out per operation type (default: out):")}</div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1.5">
              {OPS.map((op) => (
                <label key={op.key} className="flex cursor-pointer items-center gap-1.5 text-xs" title={`Allow the Prime's ${op.label} to include this instance`}>
                  <input type="checkbox" checked={!!o[op.key]} onChange={(e) => setOpt(m, op.key, e.target.checked)} data-testid={`cluster-optin-${op.key}-${m.id}`} />
                  {op.label}
                </label>
              ))}
            </div>
            <SealedPanel cid={m.cluster_id} name={m.cluster_name} isPrime={false} apps={m.apps || Object.keys(appL)} onMsg={setMsg} />
          </div>
        );
      })}
    </Card>
  );
}

function PrimePanel({ cluster, onMsg }) {
  const [op, setOp] = useState("read");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const [log, setLog] = useState([]);

  const doPreview = async () => {
    setResult(null);
    try { const { data } = await client.post(`/my-clusters/${cluster.id}/preview`, { op_type: op }); setPreview(data); }
    catch (e) { onMsg(apiErr(e)); }
  };
  const doRun = async () => {
    try { const { data } = await client.post(`/my-clusters/${cluster.id}/run`, { op_type: op }); setResult(data); setPreview(null); }
    catch (e) { onMsg(apiErr(e)); }
  };
  const toggleLog = async () => {
    if (!logOpen) { try { const { data } = await client.get(`/my-clusters/${cluster.id}/ops`); setLog(data.ops || []); } catch { /* noop */ } }
    setLogOpen((v) => !v);
  };

  return (
    <div className="rounded-xl border border-[var(--primary)]/30 bg-[var(--primary)]/5 p-3" data-testid={`cluster-prime-${cluster.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge color="info">{TL("Prime")}</Badge> {TL("This instance coordinates cluster")} <b>“{cluster.name}”</b>.
        <button onClick={toggleLog} className="ms-auto flex items-center gap-1 text-xs opacity-70 hover:opacity-100" data-testid={`cluster-log-toggle-${cluster.id}`}><ScrollText size={13} /> {logOpen ? "Hide" : "Coordination log"}</button>
      </div>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <div className="w-48"><Select label={TL("Operation")} value={op} onChange={(e) => { setOp(e.target.value); setPreview(null); setResult(null); }} data-testid={`cluster-op-select-${cluster.id}`}>
          {OPS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </Select></div>
        <Button variant="subtle" onClick={doPreview} data-testid={`cluster-preview-${cluster.id}`}><Eye size={15} /> {TL("Preview who’ll sync")}</Button>
        <Button onClick={doRun} data-testid={`cluster-run-${cluster.id}`}><Play size={15} /> {TL("Run sync")}</Button>
      </div>

      {preview && (
        <div className="mt-2 text-xs" data-testid={`cluster-preview-out-${cluster.id}`}>
          <div className="flex items-center gap-2"><Radio size={12} className="text-[var(--primary)]" /> {preview.counts.on_call} on the call · <span className="text-[#24A148]">{preview.counts.will_join} will join</span> · <span className="opacity-60">{preview.counts.will_skip} opted out</span></div>
          {preview.joined.length > 0 && <div className="mt-1"><b>{TL("Will sync:")}</b> {preview.joined.join(", ")}</div>}
          {preview.not_joined.length > 0 && <div className="opacity-70"><b>{TL("Will not:")}</b> {preview.not_joined.join(", ")}</div>}
        </div>
      )}
      {result && (
        <div className="mt-2 text-xs" data-testid={`cluster-run-out-${cluster.id}`}>
          <div className="flex items-center gap-2"><Check size={12} className="text-[#24A148]" /> {result.op_label}: {result.counts.synced}/{result.counts.on_call} synced · {result.counts.skipped} opted out</div>
          <ul className="mt-1 space-y-0.5">
            {result.results.map((r, i) => <li key={i} className={r.synced_ok ? "text-[#24A148]" : "opacity-60"}>{r.synced_ok ? "✓" : "—"} {r.label} · {r.note}</li>)}
          </ul>
        </div>
      )}
      {logOpen && (
        <div className="mt-2 max-h-48 overflow-auto rounded-md bg-black/5 p-2 text-xs dark:bg-white/5" data-testid={`cluster-log-${cluster.id}`}>
          {log.length === 0 ? <span className="opacity-60">{TL("No coordination runs yet.")}</span> : log.map((e) => (
            <div key={e.id} className="border-b border-black/5 py-1 last:border-0 dark:border-white/5">
              <div className="font-medium">{e.op_label} · {new Date(e.at).toLocaleString()}</div>
              <div className="opacity-70">On call: {e.on_call.join(", ") || "—"}</div>
              <div className="text-[#24A148]">Joined &amp; synced: {e.synced.join(", ") || "—"}</div>
              <div className="opacity-60">Did not join: {e.not_joined.join(", ") || "—"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const appLabel = { family_tree: "Family Tree", benefits: "Benefits", currency: "Currency", holdings: "Holdings" };

function SealedPanel({ cid, name, isPrime, apps, onMsg }) {
  const [open, setOpen] = useState(false);
  const [slots, setSlots] = useState([]);
  const [items, setItems] = useState([]);
  const [newSlot, setNewSlot] = useState("");
  const [app, setApp] = useState((apps && apps[0]) || "family_tree");
  const [slotId, setSlotId] = useState("");
  const [pass, setPass] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!cid) return;
    client.get(`/my-clusters/${cid}/keyslots`).then((r) => setSlots(r.data.keyslots || [])).catch(() => {});
    client.get(`/my-clusters/${cid}/sealed`).then((r) => setItems(r.data.items || [])).catch(() => {});
  }, [cid]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const activeSlots = slots.filter((s) => s.active);
  const slotLabel = (id) => slots.find((s) => s.id === id)?.label || "—";

  const addSlot = async () => {
    if (!newSlot.trim()) return;
    try { await client.post(`/my-clusters/${cid}/keyslots`, { label: newSlot.trim() }); setNewSlot(""); load(); }
    catch (e) { onMsg(apiErr(e)); }
  };
  const toggleSlot = async (s) => {
    try { await client.patch(`/my-clusters/${cid}/keyslots/${s.id}`, { active: !s.active }); load(); } catch (e) { onMsg(apiErr(e)); }
  };
  const delSlot = async (s) => {
    if (!window.confirm(`Delete key-slot "${s.label}"? Existing sealed items stay but can only be opened by holders of that passphrase.`)) return;
    try { await client.delete(`/my-clusters/${cid}/keyslots/${s.id}`); load(); } catch (e) { onMsg(apiErr(e)); }
  };

  const sendSealed = async () => {
    onMsg("");
    if (!slotId) { onMsg("Choose a key-slot to seal under."); return; }
    if (pass.length < 8) { onMsg("Passphrase must be at least 8 characters."); return; }
    setBusy(true);
    try {
      const { data } = await client.get(`/backup/${app}`);
      const env = await encryptBackup(data, pass, { app, count: data.count, exported_at: data.exported_at });
      await client.post(`/my-clusters/${cid}/sealed`, { app, keyslot_id: slotId, label: `${appLabel[app] || app} snapshot`, envelope: env });
      onMsg(`Sealed ${appLabel[app] || app} snapshot shared under "${slotLabel(slotId)}". The server cannot read it.`);
      setPass(""); load();
    } catch (e) { onMsg(apiErr(e)); } finally { setBusy(false); }
  };

  const receiveSealed = async (it) => {
    const p = window.prompt(`Enter the passphrase for key-slot "${it.keyslot_label}" to open and merge "${it.label}":`);
    if (!p) return;
    onMsg(""); setBusy(true);
    try {
      const { data } = await client.get(`/my-clusters/${cid}/sealed/${it.id}`);
      client.post(`/my-clusters/${cid}/sealed/${it.id}/ack`, { status: "received" }).catch(() => {});
      const plain = await decryptBackup(data.envelope, p);
      await client.post(`/backup/${it.app}/restore`, { ...plain, mode: "merge" });
      await client.post(`/my-clusters/${cid}/sealed/${it.id}/ack`, { status: "opened" }).catch(() => {});
      onMsg(`Opened and merged "${it.label}" into your ${appLabel[it.app] || it.app}.`);
      load();
    } catch (e) { onMsg(apiErr(e)); } finally { setBusy(false); }
  };

  const removeItem = async (it) => {
    try { await client.delete(`/my-clusters/${cid}/sealed/${it.id}`); load(); } catch (e) { onMsg(apiErr(e)); }
  };

  return (
    <div className="mt-2 rounded-lg border border-black/10 dark:border-white/10" data-testid={`sealed-panel-${cid}`}>
      <button onClick={() => setOpen((v) => !v)} data-testid={`sealed-toggle-${cid}`}
        className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium">
        <Shield size={15} className="text-[var(--primary)]" /> Sealed transfers (zero-knowledge)
        <span className="ms-auto text-xs opacity-60">{open ? "hide" : "open"}</span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-black/10 p-3 dark:border-white/10">
          <p className="text-xs opacity-65">{TL("Data is encrypted in your browser (AES-256 + Argon2id) under a key-slot passphrase. The server stores only ciphertext — the operator can never read it. Copy/merge only.")}</p>

          {isPrime && (
            <div data-testid={`keyslots-mgr-${cid}`}>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide opacity-60">{TL("Key-slots")}</div>
              <div className="space-y-1">
                {slots.map((s) => (
                  <div key={s.id} className="flex items-center gap-2 text-sm" data-testid={`keyslot-${s.id}`}>
                    <KeyRound size={13} className="opacity-60" />
                    <span className={s.active ? "" : "line-through opacity-50"}>{s.label}</span>
                    {!s.active && <Badge color="neutral">{TL("disabled")}</Badge>}
                    <button onClick={() => toggleSlot(s)} data-testid={`keyslot-toggle-${s.id}`} className="ms-auto text-xs opacity-60 hover:opacity-100">{s.active ? "disable" : "enable"}</button>
                    <button onClick={() => delSlot(s)} data-testid={`keyslot-del-${s.id}`} className="text-[#DA1E28] hover:opacity-70"><Trash2 size={13} /></button>
                  </div>
                ))}
                {slots.length === 0 && <p className="text-xs opacity-50">{TL("No key-slots yet. Create one (e.g. “Family key”).")}</p>}
              </div>
              <div className="mt-1 flex gap-2">
                <input value={newSlot} onChange={(e) => setNewSlot(e.target.value)} placeholder={TL("New key-slot label")} data-testid={`keyslot-new-${cid}`}
                  className="flex-1 rounded-md border border-black/15 dark:border-white/15 bg-transparent px-2 py-1 text-sm" />
                <Button onClick={addSlot} data-testid={`keyslot-add-${cid}`}><Plus size={14} /> {TL("Add")}</Button>
              </div>
            </div>
          )}

          <div className="rounded-md bg-black/5 p-2 dark:bg-white/5">
            <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide opacity-60"><Send size={12} /> {TL("Seal & share a snapshot")}</div>
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-36"><Select label={TL("Application")} value={app} onChange={(e) => setApp(e.target.value)} data-testid={`sealed-app-${cid}`}>
                {(apps || []).map((a) => <option key={a} value={a}>{appLabel[a] || a}</option>)}
              </Select></div>
              <div className="w-40"><Select label={TL("Key-slot")} value={slotId} onChange={(e) => setSlotId(e.target.value)} data-testid={`sealed-slot-${cid}`}>
                <option value="">{TL("— choose —")}</option>
                {activeSlots.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </Select></div>
              <input type="password" value={pass} onChange={(e) => setPass(e.target.value)} placeholder={TL("Slot passphrase")} data-testid={`sealed-pass-${cid}`}
                className="w-40 rounded-md border border-black/15 dark:border-white/15 bg-transparent px-2 py-1.5 text-sm" />
              <Button onClick={sendSealed} disabled={busy || activeSlots.length === 0} data-testid={`sealed-send-${cid}`}><Lock size={14} /> {TL("Seal & share")}</Button>
            </div>
            {activeSlots.length === 0 && <p className="mt-1 text-xs opacity-55">{isPrime ? "Create an active key-slot above first." : "Waiting for the Prime to publish an active key-slot."}</p>}
          </div>

          <div>
            <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide opacity-60"><Inbox size={12} /> Sealed items ({items.length})</div>
            {items.length === 0 ? <p className="text-xs opacity-50">{TL("No sealed items shared yet.")}</p> : (
              <ul className="space-y-1">
                {items.map((it) => (
                  <li key={it.id} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`sealed-item-${it.id}`}>
                    <Lock size={12} className="text-[var(--primary)]" />
                    <span>{it.label}</span>
                    <Badge color="neutral">{it.keyslot_label}</Badge>
                    <span className="text-xs opacity-60">from {it.from_label}</span>
                    <div className="ms-auto flex items-center gap-2">
                      <Button variant="subtle" onClick={() => receiveSealed(it)} data-testid={`sealed-open-${it.id}`}><KeyRound size={13} /> {TL("Open & merge")}</Button>
                      <button onClick={() => removeItem(it)} data-testid={`sealed-del-${it.id}`} className="text-[#DA1E28] hover:opacity-70"><Trash2 size={13} /></button>
                    </div>
                    {(it.acks || []).length > 0 && (
                      <div className="w-full text-xs opacity-60" data-testid={`sealed-acks-${it.id}`}>
                        {it.acks.map((a) => `${a.status === "opened" ? "Opened" : "Received"} by ${a.label}`).join(" \u00b7 ")}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
